import { useState, useContext, createContext, FC, useEffect } from 'react';
import { InventoryItem, Unit, Category, Bin, StringMap } from '../lib/types'
import { addDoc, collection, deleteDoc, doc, onSnapshot, setDoc, Timestamp, updateDoc } from 'firebase/firestore';
import { useFirestore, useSigninCheck } from './use-firebase';
import { CollectionsEnum } from '../lib/enums';
import { toast } from "sonner"

const InventoryContext = createContext({
  units: [] as Unit[],
  inventory: [] as InventoryItem[],
  categories: [] as Category[],
  bins: [] as Bin[],
  categoryMap: {} as StringMap,
  unitNameMap: {} as StringMap,
  unitAbbrMap: {} as StringMap,
  binLocationMap: {} as StringMap,
  addInventoryElem: async (item: Unit | InventoryItem | Category | Bin, collectionPath: CollectionsEnum) => {
    return;
  },
  deleteInventoryElem: async (itemId: string, collectionPath: CollectionsEnum) => {
    return
  },
  updateInventoryElem: async (item: Unit | InventoryItem | Category | Bin, collectionPath: CollectionsEnum) => {
    return
  },
  setInventoryElem: async (item: Unit | InventoryItem | Category | Bin, collectionPath: CollectionsEnum) => {
    return
  },
  deleteUnitWithCascade: async (unitId: string) => {
    return
  },
  deleteCategoryWithCascade: async (categoryId: string) => {
    return
  },
  deleteBinWithCascade: async (binId: string) => {
    return
  }

});

// A custom hook for global state management
export const useInventoryContext = () => {
  const ic = useContext(InventoryContext);
  if (!ic) {
    throw new Error("useInventoryContext must be used within a InventoryContextProvider");
  }
  return ic;
};

type InventoryContextProviderProps = {
  children: React.ReactNode;
}

// The provider for your state
export const InventoryContextProvider: FC<InventoryContextProviderProps> = ({ children }) => {
  // State managed with Immer
  const db = useFirestore()
  const { data: signInData } = useSigninCheck()

  const [inventory, setInventory] = useState<InventoryItem[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [units, setUnits] = useState<Unit[]>([])
  const [bins, setBins] = useState<Bin[]>([])

  const [categoryMap, setCategoryMap] = useState<StringMap>({})
  const [unitNameMap, setUnitMap] = useState<StringMap>({})
  const [unitAbbrMap, setUnitAbbrMap] = useState<StringMap>({})
  const [binLocationMap, setBinLocationMap] = useState<StringMap>({})

  //BINS SNAPSHOT INITIALIZATION
  useEffect(() => {
    if (signInData?.signedIn) {
      const unsub = onSnapshot(collection(db, `users/${signInData?.user?.uid}/bins`), (snapshot) => {
        if (snapshot.empty) {
          console.warn("No Bins Found")
          setBins([])
          setBinLocationMap({})
          return
        } else {
          const bs: Bin[] = []
          const bsMap: StringMap = {}

          for (let i = 0; i < snapshot.docs.length; i++) {
            const b = snapshot.docs[i]
            bs.push({ ...b.data(), id: b.id } as Bin)
            bsMap[b.id] = b.data().location

          }

          setBins(bs.sort((a, b) => {
            return a.location.toLowerCase() > b.location.toLowerCase() ? 1 : -1
          }))
          setBinLocationMap(bsMap)
        }
      })
      return () => unsub();
    } else {
      // Clear state when user logs out
      setBins([])
      setBinLocationMap({})
    }
  }, [signInData?.signedIn, db, signInData])


  //UNIT SNAPSHOT INITIALIZATION
  useEffect(() => {
    if (signInData?.signedIn) {
      const unsub = onSnapshot(collection(db, `users/${signInData?.user?.uid}/units`), (snapshot) => {
        if (snapshot.empty) {
          console.warn("No Units Found")
          setUnits([])
          setUnitMap({})
          setUnitAbbrMap({})
          return
        } else {
          const us: Unit[] = []
          const um: StringMap = {}
          const usm: StringMap = {}

          for (let i = 0; i < snapshot.docs.length; i++) {
            const u = snapshot.docs[i]
            us.push({ ...u.data(), id: u.id } as Unit)
            um[u.id] = u.data().name
            usm[u.id] = u.data().abbreviation
          }

          setUnits(us)
          setUnitMap(um)
          setUnitAbbrMap(usm)
        }
      })
      return () => unsub();
    } else {
      // Clear state when user logs out
      setUnits([])
      setUnitMap({})
      setUnitAbbrMap({})
    }
  }, [signInData?.signedIn, db, signInData])

  //INVENTORY SNAPSHOT INITIALIZATION
  useEffect(() => {
    if (signInData?.signedIn) {
      const unsub = onSnapshot(collection(db, `users/${signInData?.user?.uid}/inventory`), (snapshot) => {
        if (snapshot.empty) {
          console.warn("No Inventory Found")
          setInventory([])
          return
        } else {
          const inv: InventoryItem[] = []

          for (let i = 0; i < snapshot.docs.length; i++) {
            const ii = snapshot.docs[i]
            inv.push({
              ...ii.data(),
              id: ii.id,
            } as InventoryItem)
          }

          setInventory(inv)
        }
      })
      return () => unsub();
    } else {
      // Clear state when user logs out
      setInventory([])
    }
  }, [signInData?.signedIn, db, signInData])

  //CATEGORY SNAPSHOT INITIALIZATION
  useEffect(() => {
    if (signInData?.signedIn) {
      const unsub = onSnapshot(collection(db, `users/${signInData?.user?.uid}/categories`), (snapshot) => {
        if (snapshot.empty) {
          console.warn("No Categories Found")
          setCategories([])
          setCategoryMap({})
          return
        } else {
          const cats: Category[] = [];
          const catMap: StringMap = {};

          for (let i = 0; i < snapshot.docs.length; i++) {
            const c = snapshot.docs[i]
            cats.push({ ...c.data(), id: c.id } as Category)
            catMap[c.id] = c.data().name
          }

          setCategories(cats)
          setCategoryMap(catMap)
        }
      })
      return () => unsub();
    } else {
      // Clear state when user logs out
      setCategories([])
      setCategoryMap({})
    }
  }, [signInData?.signedIn, db, signInData])

  const addInventoryElem = async (item: Unit | InventoryItem | Category | Bin, collectionPath: CollectionsEnum) => {
    try {
      if (item?.id) {
        await setDoc(doc(db, `users/${signInData?.user?.uid}/${collectionPath}/${item.id}`), {
          ...item,
          createAt: Timestamp.now(),
        })
        return
      }
      await addDoc(collection(db, `users/${signInData?.user?.uid}/${collectionPath}`), {
        ...item,
        createAt: Timestamp.now(),
      })
    } catch (e) {
      console.error(e)
      toast.error("error adding to " + collectionPath)
    }
  }

  const deleteInventoryElem = async (itemId: string, collectionPath: CollectionsEnum) => {
    try {
      await deleteDoc(doc(db, `users/${signInData?.user?.uid}/${collectionPath}/${itemId}`))
    } catch (e) {
      console.error(e)
      toast.error("error removing item")
    }
  }

  const updateInventoryElem = async (item: Unit | InventoryItem | Category | Bin, collectionPath: CollectionsEnum) => {
    try {
      await updateDoc(doc(db, `users/${signInData?.user?.uid}/${collectionPath}/${item.id}`), {
        ...item,
        updatedAt: Timestamp.now(),
      })
    } catch (e) {
      console.error(e)
      toast.error("error updating element in" + collectionPath)
    }
  }

  const setInventoryElem = async (item: Unit | InventoryItem | Category | Bin, collectionPath: CollectionsEnum) => {
    try {
      await setDoc(doc(db, `users/${signInData?.user?.uid}/${collectionPath}/${item.id}`), {
        ...item,
        updatedAt: Timestamp.now(),
      })
    } catch (e) {
      console.error(e)
      toast.error("error setting element in" + collectionPath)
    }
  }

  // Cascade delete for Units: deletes bins and clears inventory references
  const deleteUnitWithCascade = async (unitId: string) => {
    try {
      const uid = signInData?.user?.uid
      if (!uid) throw new Error("No user ID")

      // 1. Find affected items and bins
      const affectedItems = inventory.filter(item => item.unit === unitId)
      const affectedBins = bins.filter(bin => bin.unit === unitId)

      // 2. Clear unit and location references on inventory items
      for (const item of affectedItems) {
        await updateDoc(doc(db, `users/${uid}/inventory/${item.id}`), {
          unit: "",
          location: "" // Clear bin too since bins will be deleted
        })
      }

      // 3. Delete all bins in this unit
      for (const bin of affectedBins) {
        await deleteDoc(doc(db, `users/${uid}/bins/${bin.id}`))
      }

      // 4. Delete the unit
      await deleteDoc(doc(db, `users/${uid}/units/${unitId}`))

      toast.success(`Unit deleted. ${affectedItems.length} item${affectedItems.length !== 1 ? 's' : ''} need reassignment.`)
    } catch (e) {
      console.error(e)
      toast.error("Error deleting unit")
    }
  }

  // Cascade delete for Categories: clears inventory references
  const deleteCategoryWithCascade = async (categoryId: string) => {
    try {
      const uid = signInData?.user?.uid
      if (!uid) throw new Error("No user ID")

      // 1. Find affected items
      const affectedItems = inventory.filter(item => item.category === categoryId)

      // 2. Clear category reference on inventory items
      for (const item of affectedItems) {
        await updateDoc(doc(db, `users/${uid}/inventory/${item.id}`), {
          category: ""
        })
      }

      // 3. Delete the category
      await deleteDoc(doc(db, `users/${uid}/categories/${categoryId}`))

      toast.success(`Category deleted. ${affectedItems.length} item${affectedItems.length !== 1 ? 's' : ''} no longer have a category.`)
    } catch (e) {
      console.error(e)
      toast.error("Error deleting category")
    }
  }

  // Cascade delete for Bins: clears inventory references
  const deleteBinWithCascade = async (binId: string) => {
    try {
      const uid = signInData?.user?.uid
      if (!uid) throw new Error("No user ID")

      // 1. Find affected items
      const affectedItems = inventory.filter(item => item.location === binId)

      // 2. Clear location reference on inventory items
      for (const item of affectedItems) {
        await updateDoc(doc(db, `users/${uid}/inventory/${item.id}`), {
          location: ""
        })
      }

      // 3. Delete the bin
      await deleteDoc(doc(db, `users/${uid}/bins/${binId}`))

      toast.success(`Bin deleted. ${affectedItems.length} item${affectedItems.length !== 1 ? 's' : ''} need a new location.`)
    } catch (e) {
      console.error(e)
      toast.error("Error deleting bin")
    }
  }

  return (
    <InventoryContext.Provider value={{
      units,
      inventory,
      categories,
      bins,
      categoryMap,
      unitNameMap,
      unitAbbrMap,
      binLocationMap,
      addInventoryElem,
      deleteInventoryElem,
      updateInventoryElem,
      setInventoryElem,
      deleteUnitWithCascade,
      deleteCategoryWithCascade,
      deleteBinWithCascade
    }}>
      {children}
    </InventoryContext.Provider>
  );
}
